import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild, inject, DestroyRef } from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';

import { AnecdotesCommentInputComponent, CommentInputValue, UserModel } from '@anecdotes/commenting';
import { BehaviorSubject, Observable, combineLatest, map } from 'rxjs';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { WindowHelperService } from '@anecdotes/utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-comment-input',
  templateUrl: './comment-input.component.html',
  styleUrls: ['./comment-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CommentInputComponent),
      multi: true,
    },
  ],
})
export class CommentInputComponent implements OnInit, ControlValueAccessor {
  protected readonly destroyRef = inject(DestroyRef);
  @ViewChild('textInput', { static: true })
  private textInput: AnecdotesCommentInputComponent;
  private termsToHighlight$ = new BehaviorSubject<string[]>([]);
  protected textControl = new UntypedFormControl(null, Validators.required);

  get value(): CommentInputValue {
    return this.textControl.value;
  }

  set value(value: CommentInputValue) {
    this.textControl.setValue(value);
  }

  isInFocus: boolean;

  overlayHtml$: Observable<SafeHtml>;

  get termsToHighlight(): string[] {
    return this.termsToHighlight$.value;
  }

  @Input()
  set termsToHighlight(value: string[]) {
    this.termsToHighlight$.next(value);
  }

  @Input()
  stickyOptions: string[] = [];

  @HostBinding('class.editable')
  @Input()
  editable = true;

  @Input()
  placeholder: string;

  @Input()
  focusOnInit: boolean;

  @Input()
  usersMap: Map<string, UserModel>;

  @Input()
  dropdownWidth: number;

  @Input()
  inputHeight: number;

  @Output()
  gotFocus = new EventEmitter();

  @Output()
  lostFocus = new EventEmitter();

  onChange: (value) => void = () => {};
  onTouch: (value) => void = () => {};

  constructor(protected cd: ChangeDetectorRef, protected windowHelper: WindowHelperService, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.overlayHtml$ = combineLatest([
      this.textControl.valueChanges as Observable<CommentInputValue>,
      this.termsToHighlight$,
    ]).pipe(
      map(([commentValue, termsToHighlight]) =>
        this.sanitizer.bypassSecurityTrustHtml(
          this.getOverlayHtml(commentValue?.body, termsToHighlight),
        ),
      ),
    );
    this.textControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => this.onChange(value));

    if (this.focusOnInit) {
      setTimeout(() => {
        this.focus();
      }, 500);
    }
  }

  linkClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const anchor = event.target as HTMLAnchorElement;

    if (anchor.href) {
      this.windowHelper.openUrlInNewTab(anchor.href);
    }
  }

  reset(): void {
    this.textControl.reset();
  }

  focus(): void {
    this.textInput.focus();
  }

  writeValue(obj: any): void {
    this.textControl.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.textControl.disable();
    } else {
      this.textControl.enable();
    }
  }

  focusIn(): void {
    this.gotFocus.emit();
    this.isInFocus = true;
    this.cd.detectChanges();
  }

  focusOut(): void {
    this.lostFocus.emit();
    this.isInFocus = false;
    this.cd.detectChanges();
  }

  private getOverlayHtml(inputHtml: string, termsToHighlight: string[]): string {
    if (!inputHtml) {
      return '';
    }

    if (!termsToHighlight) {
      return inputHtml;
    }

    let newValue = inputHtml;
    termsToHighlight.forEach((termToHighlight) => {
      newValue = newValue.replace(new RegExp(termToHighlight, 'g'), `<span class="term">${termToHighlight}</span>`);
    });

    if (newValue !== inputHtml) {
      return newValue;
    }

    return null;
  }
}
